import React, { useCallback, useEffect, useState } from 'react';
import { Select, Space, Tag, Typography } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Card } from '@app/components/common/Card/Card';
import { Button } from '@app/components/common/buttons/Button/Button';
import { InputPassword } from '@app/components/common/inputs/InputPassword/InputPassword';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import {
  AiSettings,
  AiSettingsUpdate,
  KeyProbeResult,
  SettingSource,
  getAiSettings,
  testAiKey,
  updateAiSettings,
} from '@app/api/aiSettings.api';

/**
 * AI Settings — the OpenAI key and the model/quality knobs, managed from the
 * CMS so rotating the key no longer needs a developer and a deploy
 * (client feedback 3).
 *
 * Two things this screen deliberately does NOT do:
 *
 * 1. It never shows the key. The backend only ever sends a mask
 *    (`sk-proj-…6ecd`); there is no reveal, because an admin who can replace
 *    the key still should not be able to read the one in use.
 * 2. It does not save a key without offering **Test** first. A typo'd key
 *    fails every generation at roughly one coin a time, so the probe against
 *    OpenAI is part of the flow rather than a nicety.
 */

const sourceTag = (source: SettingSource) => {
  if (source === 'db') return <Tag color="green">from database</Tag>;
  if (source === 'env') return <Tag color="blue">from .env (not yet saved here)</Tag>;
  return <Tag color="red">unreadable — re-enter the key</Tag>;
};

const changedBy = (updatedAt: string | null, updatedBy: number | null) =>
  updatedAt ? (
    <Typography.Text type="secondary">
      last changed {new Date(updatedAt).toLocaleString()}
      {updatedBy !== null ? ` by admin #${updatedBy}` : ''}
    </Typography.Text>
  ) : null;

const AiSettingsPage: React.FC = () => {
  const { isMounted } = useMounted();
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [keyDraft, setKeyDraft] = useState('');
  const [probe, setProbe] = useState<KeyProbeResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getAiSettings()
      .then((data) => {
        if (isMounted.current) setSettings(data);
      })
      .catch((e) => notificationController.error({ message: e.message }))
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, [isMounted]);

  useEffect(load, [load]);

  const save = async (body: AiSettingsUpdate, successMessage: string) => {
    setSaving(true);
    try {
      const next = await updateAiSettings(body);
      if (!isMounted.current) return;
      setSettings(next);
      notificationController.success({ message: successMessage });
    } catch (e) {
      notificationController.error({ message: (e as Error).message });
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const runProbe = async () => {
    setTesting(true);
    setProbe(null);
    try {
      // An empty draft probes the key already in use, which is the useful
      // question after someone else rotated it.
      const result = await testAiKey(keyDraft.trim() || undefined);
      if (isMounted.current) setProbe(result);
    } catch (e) {
      notificationController.error({ message: (e as Error).message });
    } finally {
      if (isMounted.current) setTesting(false);
    }
  };

  const saveKey = () => {
    const value = keyDraft.trim();
    if (!value) return;
    save({ openaiApiKey: value }, 'API key saved — the next generation uses it, no restart needed').then(() => {
      if (isMounted.current) {
        setKeyDraft('');
        setProbe(null);
      }
    });
  };

  const knob = (label: string, field: 'plannerModel' | 'imageModel' | 'imageQuality') => {
    if (!settings) return null;
    const current = settings[field];
    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <Space align="center" wrap>
          <Typography.Text strong>{label}</Typography.Text>
          {sourceTag(current.source)}
        </Space>
        <div style={{ marginTop: '0.5rem' }}>
          <Space wrap>
            <Select
              style={{ minWidth: 220 }}
              value={current.value ?? undefined}
              options={settings.allowed[field].map((v) => ({ label: v, value: v }))}
              onChange={(value) => save({ [field]: value }, `${label} saved`)}
              disabled={saving}
            />
            {current.source === 'db' && (
              <Button
                type="text"
                disabled={saving}
                onClick={() => save({ [field]: null }, `${label} reset to the .env value`)}
              >
                Reset to .env
              </Button>
            )}
          </Space>
        </div>
        <div>{changedBy(current.updatedAt, current.updatedBy)}</div>
      </div>
    );
  };

  return (
    <>
      <Card title="OpenAI API key" loading={loading} style={{ marginBottom: '1.5rem' }}>
        {settings && (
          <>
            <Space align="center" wrap style={{ marginBottom: '0.5rem' }}>
              <Typography.Text strong>In use:</Typography.Text>
              <Typography.Text code>{settings.openaiApiKey.masked ?? 'not configured'}</Typography.Text>
              {sourceTag(settings.openaiApiKey.source)}
            </Space>
            <div style={{ marginBottom: '1rem' }}>
              {changedBy(settings.openaiApiKey.updatedAt, settings.openaiApiKey.updatedBy)}
            </div>
            {settings.openaiApiKey.source === 'unreadable' && (
              <Typography.Paragraph type="danger">
                A key is stored but this environment cannot decrypt it — its SETTINGS_ENCRYPTION_KEY has changed or is
                missing. Generation is failing until a key is entered again below.
              </Typography.Paragraph>
            )}
            <Typography.Paragraph type="secondary">
              The key is never shown in full, only ever the prefix and last 4 characters. Test before saving: a wrong
              key fails every tutorial.
            </Typography.Paragraph>
            <Space.Compact style={{ width: '100%', maxWidth: 640 }}>
              {/* Masked, not a plain Input: rotating a key is exactly the
                  kind of thing done over a screen share, and the value would
                  otherwise sit in plain sight (and in browser autofill).
                  `autoComplete="off"` keeps password managers from offering to
                  save an API key as a credential. */}
              <InputPassword
                placeholder="sk-proj-…  (leave empty to test the key already in use)"
                value={keyDraft}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  setKeyDraft(e.target.value);
                  setProbe(null);
                }}
              />
              <Button type="default" loading={testing} onClick={runProbe}>
                Test
              </Button>
              <Button type="primary" loading={saving} disabled={!keyDraft.trim()} onClick={saveKey}>
                Save
              </Button>
            </Space.Compact>
            {probe && (
              <div style={{ marginTop: '0.75rem' }}>
                {probe.ok ? (
                  <Typography.Text type="success">
                    <CheckCircleOutlined /> OpenAI accepted this key.
                  </Typography.Text>
                ) : (
                  <Typography.Text type="danger">
                    <WarningOutlined /> OpenAI rejected it
                    {probe.status ? ` (${probe.status})` : ''}
                    {probe.message ? `: ${probe.message}` : ''}
                  </Typography.Text>
                )}
              </div>
            )}
            {settings.openaiApiKey.source === 'db' && (
              <div style={{ marginTop: '0.75rem' }}>
                <Button
                  type="text"
                  danger
                  disabled={saving}
                  onClick={() => save({ openaiApiKey: null }, 'Stored key cleared — the .env value applies again')}
                >
                  Clear stored key (fall back to .env)
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Generation settings" loading={loading}>
        <Typography.Paragraph type="secondary">
          Only values the backend supports are listed. Changing one takes effect on the next generation — no restart.
        </Typography.Paragraph>
        {knob('Planner model', 'plannerModel')}
        {knob('Image model', 'imageModel')}
        {knob('Image quality', 'imageQuality')}
      </Card>
    </>
  );
};

export default AiSettingsPage;
